import React, { useEffect, useState } from 'react';
import { useAuth, getApiUrl } from '../hooks/useAuth';
import { ToastContainer, toast } from 'react-toastify';

const getStatusBadgeClass = (status) =>
     status === 'Active' ? 'badge-success' : 'badge-secondary';

export default function BadgeLists() {
     const { user } = useAuth();
     const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
     const [badges, setBadges] = useState([]);
     const [loading, setLoading] = useState(false);
     const [fetchError, setFetchError] = useState('');
     const [showCreate, setShowCreate] = useState(false);
     const [showEdit, setShowEdit] = useState(false);
     const [form, setForm] = useState({
          badge_code: '',
          vendor_name: '',
          techni_id: '',
          issue_date: '',
          expiry_date: '',
          status: 'Active',
     });
     const [editForm, setEditForm] = useState({ ...form });
     const [editId, setEditId] = useState(null);
     const [formError, setFormError] = useState('');
     const [saving, setSaving] = useState(false);
     const { isAdmin, isSuperAdmin } = useAuth();
     const [technicians, setTechnicians] = useState([]);
     const [filters, setFilters] = useState({
          badge_code: '',
          vendor_name: '',
          techni_id: '',
          technician_name: '',
          branch_name: '',
          issue_date: '',
          expiry_date: '',
          status: '',
     });
     const [page, setPage] = useState(1);
     const [pageSize] = useState(10);
     const [totalPages, setTotalPages] = useState(1);

     // Fetch technicians for dropdown
     const fetchTechnicians = async () => {
          try {
               const res = await fetch(getApiUrl('/getTechnicians'), {
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
               });
               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const data = isJson ? await res.json().catch(() => ({})) : {};
               if (!res.ok) throw new Error(data?.message || 'Failed to fetch technicians');
               setTechnicians(Array.isArray(data.data) ? data.data : []);
          } catch (e) {
               console.error('Failed to fetch technicians:', e);
               setTechnicians([]);
               toast.error('Failed to fetch technicians');
          }
     };
     useEffect(() => {
          fetchTechnicians();
          // eslint-disable-next-line
     }, [token]);


     // Fetch badges
     const fetchBadges = async () => {
          setLoading(true);
          setFetchError('');
          try {
               // Build query string for filters and pagination
               const params = new URLSearchParams({
                    ...filters,
                    page,
                    pageSize,
               });
               const res = await fetch(getApiUrl(`/getBadges?${params.toString()}`), {
                    headers: {
                         Accept: 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
               });
               const isJson = (res.headers.get('content-type') || '').includes('application/json');
               const data = isJson ? await res.json().catch(() => ({})) : {};
               if (!res.ok) throw new Error(data?.message || 'Failed to fetch badges');
               setBadges(Array.isArray(data.data) ? data.data : []);
               setTotalPages(data.totalPages || 1); // backend should return totalPages
          } catch (e) {
               setFetchError(e.message || 'Failed to fetch badges');
          } finally {
               setLoading(false);
          }
     };

     useEffect(() => {
          fetchBadges();
          // eslint-disable-next-line
     }, [token, page, filters]);

     // Add Badge
     const handleSave = async () => {
          setFormError('');
          // Validation
          const required = ['badge_code', 'vendor_name', 'techni_id', 'issue_date', 'expiry_date', 'status'];
          for (let field of required) {
               if (!form[field]) {
                    setFormError(`${field.replace('_', ' ')} is required`);
                    return;
               }
          }
          setSaving(true);
          try {
               const res = await fetch(getApiUrl('/createBadge'), {
                    method: 'POST',
                    headers: {
                         Accept: 'application/json',
                         'Content-Type': 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(form),
               });
               const data = await res.json();
               if (!res.ok || data.status !== true) {
                    throw new Error(data.message || 'Failed to create badge');
               }
               toast.success('Badge created successfully');
               setShowCreate(false);
               setForm({
                    badge_code: '',
                    vendor_name: '',
                    techni_id: '',
                    issue_date: '',
                    expiry_date: '',
                    status: 'ISSUED',
               });
               fetchBadges();
          } catch (e) {
               setFormError(e.message || 'Failed to create badge');
          } finally {
               setSaving(false);
          }
     };

     // Edit Badge
     const handleEdit = async () => {
          setFormError('');
          if (!editId) return;
          const required = ['badge_code', 'vendor_name', 'techni_id', 'issue_date', 'expiry_date', 'status'];
          for (let field of required) {
               if (!editForm[field]) {
                    setFormError(`${field.replace('_', ' ')} is required`);
                    return;
               }
          }
          setSaving(true);
          try {
               const res = await fetch(getApiUrl(`/updateBadge/${editId}`), {
                    method: 'POST',
                    headers: {
                         Accept: 'application/json',
                         'Content-Type': 'application/json',
                         ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(editForm),
               });
               const data = await res.json();
               if (!res.ok || data.status !== true) {
                    throw new Error(data.message || 'Failed to update badge');
               }
               toast.success('Badge updated successfully');
               setShowEdit(false);
               setEditId(null);
               fetchBadges();
          } catch (e) {
               setFormError(e.message || 'Failed to update badge');
          } finally {
               setSaving(false);
          }
     };

     // Filter input handler
     const handleFilterChange = (e) => {
          const { name, value } = e.target;
          setFilters((prev) => ({ ...prev, [name]: value }));
          setPage(1); // reset to first page on filter change
     };

     return (
          <div>
               <ToastContainer position="top-right" />
               <div className="mb-3 d-flex justify-content-between align-items-center">
                    <h1 className="h3 mb-0 text-gray-800">TLD Badges</h1>
                    <div>
                         <input
                              type="text"
                              className="form-control"
                              placeholder="Search badges..."
                              value={filters.search || ''}
                              onChange={e => {
                                   setFilters(f => ({ ...f, search: e.target.value }));
                                   setPage(1);
                              }}
                              style={{ width: 300, display: 'inline-block' }}
                         />
                    </div>
                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                         <i className="fas fa-plus mr-2" /> Add Badge
                    </button>
               </div>

               <div className="card shadow mb-4">
                    <div className="card-body">
                         <div className="table-responsive">
                              <table className="table table-bordered" width="100%" cellSpacing="0">
                                   <thead>
                                        <tr>
                                             <th>Badge Code</th>
                                             <th>Vendor Name</th>
                                             <th>Technician </th>
                                             <th>Branch </th>
                                             <th>Issue Date</th>
                                             <th>Expiry Date</th>
                                             <th>Status</th>
                                             <th></th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {badges.length === 0 ? (
                                             <tr>
                                                  <td colSpan="7" className="text-center p-4">No badges found</td>
                                             </tr>
                                        ) : (
                                             badges.map((b) => (
                                                  <tr key={b.badge_id || b.id}>
                                                       <td>{b.badge_code}</td>
                                                       <td>{b.vendor_name}</td>
                                                       <td>{b.technician_name}</td>
                                                       <td>{b.branch_name}</td>
                                                       <td>{b.issue_date}</td>
                                                       <td>{b.expiry_date}</td>
                                                       <td>
                                                            <span className={`badge ${getStatusBadgeClass(b.status)}`}>
                                                                 {b.status}
                                                            </span>
                                                       </td>
                                                       <td>
                                                            <button
                                                                 className="btn btn-sm btn-outline-primary"
                                                                 onClick={() => {
                                                                      setEditForm({ ...b });
                                                                      setEditId(b.badge_id || b.id);
                                                                      setShowEdit(true);
                                                                      setFormError('');
                                                                 }}
                                                            >
                                                                 <i className="fas fa-edit"></i> Edit
                                                            </button>
                                                       </td>
                                                  </tr>
                                             ))
                                        )}
                                   </tbody>
                              </table>
                         </div>
                         <nav>
                              <ul className="pagination mb-0 justify-content-left">
                                   <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                                        <button
                                             className="page-link"
                                             onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        >
                                             &laquo;
                                        </button>
                                   </li>
                                   {[...Array(totalPages)].map((_, idx) => (
                                        <li
                                             key={idx + 1}
                                             className={`page-item${page === idx + 1 ? " active" : ""}`}
                                        >
                                             <button
                                                  className="page-link"
                                                  onClick={() => setPage(idx + 1)}
                                             >
                                                  {idx + 1}
                                             </button>
                                        </li>
                                   ))}
                                   <li className={`page-item${page === totalPages ? " disabled" : ""}`}>
                                        <button
                                             className="page-link"
                                             onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        >
                                             &raquo;
                                        </button>
                                   </li>
                              </ul>
                         </nav>
                    </div>
               </div>

               {/* Add Badge Modal */}
               {showCreate && (
                    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                         <div className="modal-dialog modal-lg">
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Add New Badge</h5>
                                        <button
                                             type="button"
                                             className="close"
                                             onClick={() => {
                                                  setShowCreate(false);
                                                  setFormError('');
                                             }}
                                        >
                                             <span>&times;</span>
                                        </button>
                                   </div>
                                   <div className="modal-body">
                                        {formError && (
                                             <div className="alert alert-danger py-2">{formError}</div>
                                        )}
                                        <form onSubmit={e => e.preventDefault()}>
                                             <div className='row'>
                                                  <div className="form-group col-md-6">
                                                       <label>Badge Code</label>
                                                       <input
                                                            type="text"
                                                            className="form-control"
                                                            value={form.badge_code}
                                                            onChange={e => setForm(f => ({ ...f, badge_code: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-6">
                                                       <label>Vendor Name</label>
                                                       <input
                                                            type="text"
                                                            className="form-control"
                                                            value={form.vendor_name}
                                                            onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-12">
                                                       <label>Technician</label>
                                                       <select
                                                            className="form-control"
                                                            value={form.techni_id}
                                                            onChange={e => setForm(f => ({ ...f, techni_id: e.target.value }))}
                                                            required
                                                       >
                                                            <option value="">Select Technician</option>
                                                            {technicians.map(t => (
                                                                 <option key={t.techni_id} value={t.techni_id}>
                                                                      {t.name} ({t.emp_code}) - {t.branch_name}
                                                                 </option>
                                                            ))}
                                                       </select>
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Issue Date</label>
                                                       <input
                                                            type="date"
                                                            className="form-control"
                                                            value={form.issue_date}
                                                            onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Expiry Date</label>
                                                       <input
                                                            type="date"
                                                            className="form-control"
                                                            value={form.expiry_date}
                                                            onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Status</label>
                                                       <select
                                                            className="form-control"
                                                            value={editForm.status}
                                                            readOnly={true}
                                                       >
                                                            <option value="ISSUED">ISSUED</option>
                                                            <option value="INBRANCH">INBRANCH</option>
                                                            <option value="RETURNED">RETURNED</option>
                                                            <option value="LOST">LOST</option>
                                                            <option value="ANALYSIS">ANALYSIS</option>
                                                       </select>
                                                  </div>
                                             </div>
                                        </form>
                                   </div>
                                   <div className="modal-footer">
                                        <button
                                             type="button"
                                             className="btn btn-secondary"
                                             onClick={() => setShowCreate(false)}
                                             disabled={saving}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             type="button"
                                             className="btn btn-primary"
                                             onClick={handleSave}
                                             disabled={saving}
                                        >
                                             {saving ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                                       Saving...
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-save mr-2" />
                                                       Add Badge
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}
               {showEdit && (
                    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                         <div className="modal-dialog modal-lg">
                              <div className="modal-content">
                                   <div className="modal-header">
                                        <h5 className="modal-title">Edit Badge</h5>
                                        <button
                                             type="button"
                                             className="close"
                                             onClick={() => {
                                                  setShowEdit(false);
                                                  setEditId(null);
                                                  setFormError('');
                                             }}
                                        >
                                             <span>&times;</span>
                                        </button>
                                   </div>
                                   <div className="modal-body">
                                        {formError && (
                                             <div className="alert alert-danger py-2">{formError}</div>
                                        )}
                                        <form onSubmit={e => e.preventDefault()}>
                                             <div className="row">
                                                  <div className="form-group col-md-6">
                                                       <label>Badge Code</label>
                                                       <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editForm.badge_code}
                                                            onChange={e => setEditForm(f => ({ ...f, badge_code: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-6">
                                                       <label>Vendor Name</label>
                                                       <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editForm.vendor_name}
                                                            onChange={e => setEditForm(f => ({ ...f, vendor_name: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-12">
                                                       <label>Technician</label>
                                                       <select
                                                            className="form-control"
                                                            value={editForm.techni_id}
                                                            readOnly={true}
                                                       >
                                                            <option value="">Select Technician</option>
                                                            {technicians.map(t => (
                                                                 <option key={t.techni_id} value={t.techni_id}>
                                                                      {t.name} ({t.emp_code}) - {t.branch_name}
                                                                 </option>
                                                            ))}
                                                       </select>
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Issue Date</label>
                                                       <input
                                                            type="date"
                                                            className="form-control"
                                                            value={editForm.issue_date}
                                                            onChange={e => setEditForm(f => ({ ...f, issue_date: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Expiry Date</label>
                                                       <input
                                                            type="date"
                                                            className="form-control"
                                                            value={editForm.expiry_date}
                                                            onChange={e => setEditForm(f => ({ ...f, expiry_date: e.target.value }))}
                                                            required
                                                       />
                                                  </div>
                                                  <div className="form-group col-md-4">
                                                       <label>Status</label>
                                                       <select
                                                            className="form-control"
                                                            value={editForm.status}
                                                            onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                                                            required
                                                       >
                                                            <option value="ISSUED">ISSUED</option>
                                                            <option value="INBRANCH">INBRANCH</option>
                                                            <option value="RETURNED">RETURNED</option>
                                                            <option value="LOST">LOST</option>
                                                            <option value="ANALYSIS">ANALYSIS</option>
                                                       </select>
                                                  </div>
                                             </div>
                                        </form>
                                   </div>
                                   <div className="modal-footer">
                                        <button
                                             type="button"
                                             className="btn btn-secondary"
                                             onClick={() => {
                                                  setShowEdit(false);
                                                  setEditId(null);
                                                  setFormError('');
                                             }}
                                             disabled={saving}
                                        >
                                             Cancel
                                        </button>
                                        <button
                                             type="button"
                                             className="btn btn-primary"
                                             onClick={handleEdit}
                                             disabled={saving}
                                        >
                                             {saving ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                                                       Saving...
                                                  </>
                                             ) : (
                                                  <>
                                                       <i className="fas fa-save mr-2" />
                                                       Update Badge
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}
          </div>
     );
}